import { useFocusEffect, useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import {
  CalendarCheck,
  ChevronRight,
  Clock,
  FileText,
  Home as HomeIcon,
  Plus,
  RefreshCw,
  ShieldAlert,
  Sparkles,
  Trash2,
} from 'lucide-react-native';
import { ComponentType, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { registerScrollToTop } from '@/lib/scroll-bus';
import { useConfirm, useNotice } from '@/components/ConfirmDialog';
import { ReplanSummaryModal } from '@/components/ReplanSummaryModal';
import { NotesCard } from '@/components/NotesCard';
import { NoteEditorModal } from '@/components/NoteEditorModal';
import { addNote, deleteNote, listNotes, updateNote } from '@/lib/notes';
import { listQuotes } from '@/lib/quotes';
import { loadTimeline, type TimelineEntry, type TimelineKind } from '@/lib/timeline';
import type { NoteRow } from '@/db/types';
import type { ReplanDiff } from '@/lib/tasks';
import { HScroll } from '@/components/HScroll';
import { SectionLabel } from '@/components/home/SectionLabel';
import { HealthCard } from '@/components/my-home/HealthCard';
import { StaticMapCard } from '@/components/StaticMapCard';
import { goTo } from '@/lib/nav';
import { getActionByKey, parseToolbarKeys } from '@/lib/quick-toolbar';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { CLIMATE_ZONE_LABEL } from '@/data/climate-zip';
import { getSubtypeMeta, getSystemMeta } from '@/data/systems';
import { now } from '@/db/ids';
import type { PropertyRow, SystemRow } from '@/db/types';
import { useDataRevision, bumpDataRevision } from '@/lib/data-revision';
import { listContacts } from '@/lib/contacts';
import { addDocument, listDocumentsForProperty } from '@/lib/documents';
import { listInventory } from '@/lib/inventory';
import { exportAnnualTasksPdf, exportBinderPdf } from '@/lib/pdf';
import {
  getActiveProperty,
  getActiveTasks,
  getAnnualChecklistTasks,
  getServiceHistoryForProperty,
  getSubtypeCoverageSignal,
  getUnconfirmedSeasonalCount,
  getSystemsForProperty,
  getPropertyContentSummary,
  formatContentSummary,
  listProperties,
  profileAttributeChecks,
  setActiveProperty,
} from '@/lib/tasks';
import { resolveIcon } from '@/lib/rule-icon';
import { seasonalLabel } from '@/lib/seasonal';

const HOME_TYPE_LABEL: Record<string, string> = {
  house: 'Single-family house',
  town: 'Townhouse / row home',
  condo: 'Condo / apartment',
  mobile: 'Mobile / manufactured',
};
const HVAC_LABEL: Record<string, string> = {
  furnace: 'Forced-air furnace + AC',
  boiler: 'Boiler + radiators',
  heatpump: 'Heat pump',
  mini: 'Mini-split system',
  baseboard: 'Electric baseboard',
  radiant: 'Radiant floor heat',
  unsure: 'HVAC — to be determined',
};
const LOT_LABEL: Record<string, string> = {
  q: 'Under ¼ acre',
  '1': '¼ – 1 acre',
  '5': '1 – 5 acres',
  '20': '5 – 20 acres',
  '20+': '20+ acres',
};
const YEAR_LABEL: Record<string, string> = {
  pre60: 'Pre-1960 build',
  '60-80': '1960s–1980s build',
  '90-00': '1990s–2000s build',
  '2010+': '2010+ build',
  unsure: 'Build year unknown',
};

export default function MyHome() {
  const router = useRouter();
  const db = useSQLiteContext();
  const [property, setProperty] = useState<PropertyRow | null>(null);
  const [systems, setSystems] = useState<SystemRow[]>([]);
  const [reminderCounts, setReminderCounts] = useState<Record<string, number>>({});
  const [homeCount, setHomeCount] = useState(1);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [annualBusy, setAnnualBusy] = useState(false);
  const [tuneBadge, setTuneBadge] = useState<number>(0);
  // Replan preview modal — opens with a dry-run diff (what WOULD
  // change), user confirms via "Replan now" to actually persist. The
  // modal itself becomes the confirmation step instead of a separate
  // confirm dialog.
  const [replanSummary, setReplanSummary] = useState<ReplanDiff | null>(null);
  const [replanLoading, setReplanLoading] = useState(false);
  // Recent activity — Wave 35: now sources from the FULL timeline
  // feed (completions, skips, expenses, systems, inventory, documents,
  // contacts, notes) so the card is a true "everything that happened
  // to this home" preview, not just task completions. Cap at 12 entries
  // — enough to feel substantive on desktop without dominating mobile.
  const [recentActivity, setRecentActivity] = useState<TimelineEntry[]>([]);
  // Notes — sit beside Recent activity. Editor modal lifts to this
  // page so both compose and edit share the same Save handler.
  const [notes, setNotes] = useState<NoteRow[]>([]);
  const [noteEditor, setNoteEditor] = useState<{ visible: boolean; note: NoteRow | null }>({
    visible: false,
    note: null,
  });
  const confirm = useConfirm();
  const notice = useNotice();
  const myHomeScrollRef = useRef<ScrollView | null>(null);
  const resetScroll = useCallback(() => {
    myHomeScrollRef.current?.scrollTo({ y: 0, animated: false });
  }, []);
  useEffect(() => registerScrollToTop(resetScroll), [resetScroll]);

  const fetch = useCallback(async () => {
    const prop = await getActiveProperty(db);
    setProperty(prop);
    // Track home count so the Delete-home button can disable itself
    // when this is the user's only home (deleting it would leave the
    // app with no active property — better to nudge user to add
    // another first).
    const allHomes = await listProperties(db);
    setHomeCount(allHomes.length);
    if (prop) {
      const sys = await getSystemsForProperty(db, prop.id);
      setSystems(sys);
      // Tune badge — sum of blank profile attributes + subtype-eligible
      // systems waiting for a chip pick. Drives the small number on the
      // Tune icon button so the user sees at a glance how much there is
      // left to refine. Hidden when zero.
      const attrChecks = profileAttributeChecks(prop);
      const blanks = attrChecks.filter((c) => !c.filled).length;
      const sub = await getSubtypeCoverageSignal(db, prop.id);
      const subOpen = Math.max(
        0,
        sub.subtypeEligibleSystems - sub.subtypesSelected
      );
      // Wave 43: include unconfirmed seasonal systems so the badge
      // matches what Tune will actually surface. Was previously
      // counting blanks+subtypes only, which led to "badge says 1 but
      // nothing to tune" when the only outstanding item was a
      // seasonal review or a non-tuneable field.
      const seasonalUnconfirmed = await getUnconfirmedSeasonalCount(db, prop.id);
      setTuneBadge(blanks + subOpen + seasonalUnconfirmed);
      // Single query for all active reminders across the property's
      // systems — cheaper than N per-row queries.
      const counts: Record<string, number> = {};
      const rows = await db.getAllAsync<{ system_id: string; n: number }>(
        `SELECT system_id, COUNT(*) AS n
         FROM system_reminder
         WHERE deleted_at IS NULL AND fire_at > ?
         GROUP BY system_id`,
        Date.now()
      );
      for (const r of rows) counts[r.system_id] = r.n;
      // Include the legacy single-slot reminder if present.
      for (const s of sys) {
        if (s.reminder_at && s.reminder_at > Date.now()) {
          counts[s.id] = (counts[s.id] ?? 0) + 1;
        }
      }
      setReminderCounts(counts);
      // Recent activity feed — Wave 35: pull the full multi-kind
      // timeline so the My Home card mirrors what /timeline shows.
      // Cap at 12 — fits comfortably on desktop without making the
      // mobile scroll dominant.
      const timeline = await loadTimeline(db, prop.id, 12);
      setRecentActivity(timeline);
      // Notes — same property, separate query. Cap to a reasonable
      // preview pool; /notes pulls the full set.
      const noteRows = await listNotes(db, prop.id, 50);
      setNotes(noteRows);
    } else {
      setSystems([]);
      setReminderCounts({});
      setRecentActivity([]);
      setNotes([]);
    }
  }, [db]);

  useFocusEffect(
    useCallback(() => {
      fetch();
      resetScroll();
    }, [fetch, resetScroll])
  );

  // Re-fetch when the data revision bumps — covers home-switch (the
  // HomeSwitcher calls bumpDataRevision after setActiveProperty),
  // property edits, etc. Without this, the user switches home from the
  // sidebar and the My Home content keeps showing the previous home's
  // systems until they navigate away and back.
  const revision = useDataRevision();
  useEffect(() => {
    void fetch();
  }, [revision, fetch]);

  /**
   * Resources tile press handlers. Each resource is home-specific
   * (operates on the active property's data); the user clicked the
   * tile WHILE looking at THIS home, so we don't need to pass a
   * property id explicitly — every downstream API reads via
   * getActiveProperty.
   */
  const onExportPdf = useCallback(async () => {
    if (!property || pdfBusy) return;
    setPdfBusy(true);
    try {
      const [sys, contacts, documents, history, inventory, notesList, quotes] = await Promise.all([
        getSystemsForProperty(db, property.id),
        listContacts(db, property.id),
        listDocumentsForProperty(db, property.id),
        getServiceHistoryForProperty(db, property.id, 500),
        listInventory(db, property.id),
        listNotes(db, property.id, 500),
        listQuotes(db, property.id),
      ]);
      const res = await exportBinderPdf({
        property,
        systems: sys,
        contacts,
        documents,
        history,
        inventory,
        notes: notesList,
        quotes,
      });
      // Register the PDF as a Document so Android users have a
      // reliable retrieval path even if the share sheet was dismissed.
      if (res.success && res.fileUri && Platform.OS !== 'web') {
        try {
          await addDocument(db, {
            propertyId: property.id,
            kind: 'manual',
            title: `Home Record · ${new Date().toLocaleDateString(undefined, {
              month: 'short', day: 'numeric', year: 'numeric',
            })}`,
            file_uri: res.fileUri,
          });
        } catch {
          // Best-effort; not fatal if the document insert fails.
        }
      }
    } finally {
      setPdfBusy(false);
    }
  }, [db, property, pdfBusy]);

  /** Annual home tasks — printable Jan-Dec checklist. Uses
   *  getAnnualChecklistTasks which merges live DB rows (so completions +
   *  custom additions show through) with a SYNTHETIC full-year plan (so
   *  months that already passed before onboarding still print with
   *  their seasonal/annual maintenance reminders — what makes this a
   *  useful printable reference rather than just a snapshot of the
   *  live queue). */
  const onExportAnnual = useCallback(async () => {
    if (!property || annualBusy) return;
    setAnnualBusy(true);
    try {
      const year = new Date().getFullYear();
      const tasks = await getAnnualChecklistTasks(db, property.id, year);
      await exportAnnualTasksPdf({
        property,
        tasks: tasks.map((t) => ({
          id: t.id,
          title: t.title,
          due_at: t.due_at,
          time_minutes: t.time_minutes,
        })),
      });
    } finally {
      setAnnualBusy(false);
    }
  }, [db, property, annualBusy]);

  /** Tune my plan — same destination as the /profile-setup walkthrough,
   *  but the title-bar icon button confirms first so a misclick on the
   *  busy top row doesn't yank the user out of their current context. */
  const onTune = useCallback(async () => {
    const ok = await confirm({
      title: 'Tune my plan',
      message:
        'Quick chip walkthrough — pick the variants for any systems we still need detail on, plus blank profile fields. Takes a minute or two; you can skip any card.',
      confirmLabel: 'Open Tune',
    });
    if (!ok) return;
    router.push('/profile-setup' as never);
  }, [confirm, router]);

  /** Replan year: regenerate this home's task plan against today's
   *  property attributes. Lives on the home (not a global tool) because
   *  replanning one home shouldn't touch another. */
  const onReplan = useCallback(async () => {
    if (!property) return;
    // Open the preview modal immediately with a loading state, then
    // compute the dry-run diff in the background. User sees the
    // spinner briefly then the actual items-to-add/remove preview.
    // No more pre-confirm dialog — the modal IS the confirmation.
    setReplanLoading(true);
    setReplanSummary({ added: 0, removed: 0, rescheduled: 0, addedTitles: [], removedTitles: [], rescheduledTitles: [] });
    const { previewReplanDiff } = await import('@/lib/tasks');
    const diff = await previewReplanDiff(db, property.id);
    setReplanSummary(diff);
    setReplanLoading(false);
  }, [db, property]);

  // Confirm — actually run the regen. Called when the user taps
  // "Replan now" inside the preview modal. Closes the modal on
  // completion since the user already saw the preview.
  const onConfirmReplan = useCallback(async () => {
    if (!property) return;
    try {
      const { regenerateTasksWithDiff } = await import('@/lib/tasks');
      await regenerateTasksWithDiff(db, property.id);
      bumpDataRevision();
    } finally {
      setReplanSummary(null);
      setReplanLoading(false);
    }
  }, [db, property]);

  /**
   * Delete THIS home. Disabled when it's the user's only home — the
   * empty-app state isn't something we want to land users in by
   * accident; they can add another home first, then delete this one
   * if they really mean to. Cascade DELETE on the foreign keys takes
   * care of systems/tasks/contacts/etc.
   */
  const onDeleteHome = useCallback(async () => {
    if (!property) return;
    if (homeCount <= 1) {
      void notice({
        title: "Can't delete your only home",
        message: 'Add another home first if you want to remove this one.',
        tone: 'warning',
      });
      return;
    }
    const nickname = property.name?.trim() || 'this home';
    // Itemize the data that's about to vanish so the user sees the
    // exact cost — much more concrete than the old generic "every
    // task, system, contact..." paragraph.
    const summary = await getPropertyContentSummary(db, property.id);
    const summaryLine = formatContentSummary(summary);
    const ok = await confirm({
      title: `Delete ${nickname}?`,
      message:
        `You'll permanently lose: ${summaryLine}.\n\n` +
        `This can't be undone. Consider exporting the Home Record File first if you want a printable archive.`,
      confirmLabel: 'Delete home',
      destructive: true,
    });
    if (!ok) return;
    const t = now();
    // Soft-delete the property; cascades aren't ON DELETE here (foreign
    // keys are RESTRICT-style in places) so we soft-delete the property
    // row + flip active home to the next-oldest. The systems/tasks/etc
    // continue to reference the dead property but are filtered out by
    // every getXxxForProperty call because they're scoped by the
    // active property id.
    await db.runAsync(
      `UPDATE property SET deleted_at = ?, updated_at = ? WHERE id = ?`,
      t,
      t,
      property.id
    );
    const remaining = await listProperties(db);
    if (remaining.length === 1) {
      // Only one home left — auto-switch silently and stay on My Home.
      await setActiveProperty(db, remaining[0].id);
      bumpDataRevision();
      router.replace('/(tabs)/my-home' as never);
      return;
    }
    // 2+ remain: provisionally switch to the oldest so the UI has a
    // valid active home, then route to Home Manager (/homes) where
    // the user can pick which home to enter from a full-page list.
    // The popup picker felt clipped on big monitors; the dedicated
    // page is roomier and lets the user audit the rest of the homes
    // before deciding.
    if (remaining[0]) await setActiveProperty(db, remaining[0].id);
    bumpDataRevision();
    router.replace('/homes' as never);
  }, [db, property, homeCount, confirm, notice, router]);

  const { inside, outside } = useMemo(() => {
    const inside: SystemRow[] = [];
    const outside: SystemRow[] = [];
    for (const s of systems) (s.location === 'inside' ? inside : outside).push(s);
    return { inside, outside };
  }, [systems]);

  if (!property) return <View style={styles.container} />;

  const climateLabel = property.climate_zone
    ? `${CLIMATE_ZONE_LABEL[property.climate_zone]} climate`
    : 'Climate not set';

  return (
    <View style={styles.container}>
      <ScrollView
        ref={myHomeScrollRef}
        contentContainerStyle={{ paddingBottom: 110 }}
        showsVerticalScrollIndicator={false}>
        <View style={styles.header}>
          {/*
            Title doubles as the mobile home-switcher trigger. The
            up/down chevron after the home name hints at "I'm a picker."
            If there's no nickname set, fall back to "My Home" so the
            UI still reads cleanly during onboarding.
          */}
          {/*
            Title row with the home name on the left and a 4-icon menu
            on the right. The title takes `flex: 1` + `numberOfLines={1}`
            so even very long nicknames truncate gracefully instead of
            pushing the icons off-screen. Each input where the name is
            captured caps at 30 chars (enforced via maxLength on the
            TextInput) which keeps the menu visible on every viewport.
            We also added an "Add home" shortcut alongside Switch — a
            small redundancy with the switcher's "+" but a faster path
            for users who already know they want to add another home.
            Tune + Replan trigger confirmations before executing.
          */}
          <View style={styles.titleRow}>
            <Text style={styles.h1} numberOfLines={1}>
              {property.name?.trim() || 'My Home'}
            </Text>
            <View style={styles.titleIcons}>
              {/* Switch removed — the top-bar brand area handles multi-home
                  switching now and exists on every page, so a dedicated
                  */}
              {/* Edit removed (Wave 61, user call) — the Homes manager
                  and Settings → Home settings both reach the editor. */}
              <TitleIconBtn
                Icon={HomeIcon}
                accessibilityLabel="Open Home Manager"
                label="Homes"
                onPress={() => goTo(router, '/homes')}
              />
              <TitleIconBtn
                Icon={Sparkles}
                accessibilityLabel="Tune my plan"
                label="Tune"
                // Forest by default; flips to honey only when there's
                // actually something to tune. Mirrors the Replan icon
                // pattern — color = "you need to act," not "look at me."
                tone={tuneBadge > 0 ? 'honey' : 'forest'}
                badge={tuneBadge}
                onPress={onTune}
              />
              <TitleIconBtn
                Icon={RefreshCw}
                accessibilityLabel={
                  property.needs_replan === 1
                    ? 'Replan year — changes pending'
                    : 'Replan year'
                }
                label="Replan"
                // Honey tone + a small "!" badge when planner-affecting
                // fields changed since the last regen. Mirrors the Tune
                // badge style so the two attention-grabbing icons in the
                // bar read as a matched pair.
                tone={property.needs_replan === 1 ? 'honey' : 'forest'}
                badge={property.needs_replan === 1 ? 1 : undefined}
                onPress={onReplan}
              />
            </View>
          </View>
          <Text style={styles.sub}>The manual you&apos;re building.</Text>
        </View>

        <StaticMapCard
          lat={property.lat}
          lon={property.lon}
          address={property.address}
        />

        <HealthCard />

        {/* Quick toolbar is always on now (the toggle was removed from
            Settings). Users still control which actions appear via
            Settings → "Manage quick toolbar." */}
        <HScroll contentStyle={styles.quickRow}>
          {parseToolbarKeys(property.quick_toolbar_keys_json).map((key) => {
            const a = getActionByKey(key);
            if (!a) return null;
            return (
              <QuickBtn
                key={a.key}
                label={a.label}
                Icon={a.Icon}
                primary={a.primary}
                onPress={() => router.push(a.route as never)}
              />
            );
          })}
        </HScroll>

        {/* Add system moved up — it's the most-frequent "do something to
            this home" action, deserves prime real estate right under the
            quick toolbar. Old position (below the docs+info merged block)
            buried it under a wall of read-only content. */}
        <Pressable
          onPress={() => router.push('/system/new' as never)}
          style={({ pressed }) => [
            styles.addBtnTop,
            pressed && { transform: [{ scale: 0.98 }] },
          ]}>
          <Plus size={18} color={Palette.cream} strokeWidth={2} />
          <Text style={styles.addText}>Add a system or equipment</Text>
        </Pressable>

        {/*
          Merged "your home" block — property summary LEFT, documents
          RIGHT on web. Wrapped in a single rounded container with a
          subtle vertical divider so the two halves read as one card,
          not two. On mobile they stack and the divider becomes a
          horizontal rule (handled by `cardDivider`).

          Each document tile carries a small "PDF" badge in its top-right
          corner so the user knows at a glance these are printables —
          replaces the explicit section header that we dropped.
        */}
        <View style={styles.homeCard}>
          <View style={styles.homeCardDocs}>
            <ImportantDocTile
              label={pdfBusy ? 'Working…' : 'Home Record File'}
              sub="Full record — insurance, refi, resale"
              Icon={FileText}
              bg={Palette.moss}
              onPress={onExportPdf}
              disabled={pdfBusy}
            />
            <ImportantDocTile
              label="Emergency Card"
              sub="Shutoffs + contacts · 1-page printable"
              Icon={ShieldAlert}
              bg={Palette.clay}
              onPress={() => router.push('/emergency-card' as never)}
            />
            <ImportantDocTile
              label={annualBusy ? 'Working…' : 'Annual Home Checklist'}
              sub="Jan–Dec checklist · tick them off as you go"
              Icon={CalendarCheck}
              bg={Palette.honeyDeep}
              onPress={onExportAnnual}
              disabled={annualBusy}
            />
          </View>

          <View style={styles.homeCardDivider} />

          <View style={styles.homeCardInfo}>
            {(() => {
              const items: { label: string; value: string }[] = [
                { label: 'Climate', value: climateLabel },
                { label: 'ZIP', value: property.zip ?? '—' },
                { label: 'Home type', value: HOME_TYPE_LABEL[property.home_type ?? ''] ?? '—' },
                { label: 'Build', value: YEAR_LABEL[property.year_band ?? ''] ?? '—' },
                { label: 'Size', value: property.sqft ? `${property.sqft.toLocaleString()} sq ft` : '—' },
                { label: 'HVAC', value: HVAC_LABEL[property.hvac_type ?? ''] ?? '—' },
              ];
              if (property.secondary_hvac_type) {
                items.push({ label: '2nd HVAC', value: HVAC_LABEL[property.secondary_hvac_type] });
              }
              items.push({ label: 'Lot', value: LOT_LABEL[property.lot_band ?? ''] ?? '—' });
              const rows: { left: typeof items[number]; right?: typeof items[number] }[] = [];
              for (let i = 0; i < items.length; i += 2) {
                rows.push({ left: items[i], right: items[i + 1] });
              }
              return rows.map((r, i) => (
                <View
                  key={i}
                  style={[styles.summaryRow2, i === rows.length - 1 && { borderBottomWidth: 0 }]}>
                  <SummaryCell label={r.left.label} value={r.left.value} />
                  {r.right ? (
                    <SummaryCell label={r.right.label} value={r.right.value} />
                  ) : (
                    <View style={styles.summaryCell} />
                  )}
                </View>
              ));
            })()}
          </View>
        </View>

        {/* Recent activity — Wave 35: now full-width and pulls from the
            mixed timeline feed (completions, expenses, systems, notes,
            etc.) instead of just task completions. On desktop the rows
            render in 2 columns to take advantage of the wider canvas
            without forcing the user to scroll a long single column. */}
        <View style={styles.activityCard}>
          <Pressable
            onPress={() => router.push('/timeline' as never)}
            style={({ pressed }) => [
              styles.activityHead,
              pressed && { opacity: 0.7 },
            ]}>
            <View style={styles.activityHeadLeft}>
              <View style={styles.activityHeadIcon}>
                <Clock size={14} color={Palette.forest} strokeWidth={2} />
              </View>
              <View>
                <Text style={styles.activityHeadTitle}>Recent activity</Text>
                <Text style={styles.activityHeadSub}>
                  {recentActivity.length > 0
                    ? `Tasks, notes, expenses, systems — last ${recentActivity.length}`
                    : 'Nothing logged yet'}
                </Text>
              </View>
            </View>
            <View style={styles.activityHeadRight}>
              <Text style={styles.activityHeadLink}>Full timeline</Text>
              <ChevronRight size={13} color={Palette.forest} strokeWidth={2} />
            </View>
          </Pressable>
          {recentActivity.length === 0 ? (
            <View style={styles.activityEmpty}>
              <Text style={styles.activityEmptyText}>
                Once you start moving — completing tasks, jotting notes,
                logging expenses — it&apos;ll all surface here. Tap to drill
                into anything; tap the header for the full timeline.
              </Text>
            </View>
          ) : (
            <View style={styles.activityGrid}>
              {recentActivity.map((entry) => (
                <ActivityRow
                  key={entry.id}
                  entry={entry}
                  onPress={() => navigateForActivity(router, entry)}
                />
              ))}
            </View>
          )}
        </View>

        {/*
          Inside + Outside system previews. Wrapped in a single flex-wrap
          row so they sit side-by-side on web (each column gets `flex: 1`
          with `minWidth: 320`) and stack naturally on mobile. The count
          on each section label is now tappable — single tap jumps the
          user into the dedicated /systems page filtered by location so
          a long list (31 systems!) is one tap from search + filter.
        */}
        <View style={styles.systemsRow}>
          <View style={styles.systemsCol}>
            <SectionLabel
              title="Inside"
              hint={`${inside.length} systems`}
              onHintPress={() =>
                router.push({ pathname: '/systems', params: { loc: 'inside' } } as never)
              }
            />
            <View style={styles.list}>
              {inside.length === 0 ? (
                <EmptyHint>No indoor systems yet — add one above.</EmptyHint>
              ) : (
                inside
                  .slice(0, PREVIEW_COUNT)
                  .map((s) => (
                    <SystemRowView
                      key={s.id}
                      system={s}
                      reminderCount={reminderCounts[s.id] ?? 0}
                      onPress={() => router.push(`/system/${s.id}` as never)}
                    />
                  ))
              )}
            </View>
          </View>

          <View style={styles.systemsCol}>
            <SectionLabel
              title="Outside"
              hint={`${outside.length} systems`}
              onHintPress={() =>
                router.push({ pathname: '/systems', params: { loc: 'outside' } } as never)
              }
            />
            <View style={styles.list}>
              {outside.length === 0 ? (
                <EmptyHint>No outdoor systems yet.</EmptyHint>
              ) : (
                outside
                  .slice(0, PREVIEW_COUNT)
                  .map((s) => (
                    <SystemRowView
                      key={s.id}
                      system={s}
                      reminderCount={reminderCounts[s.id] ?? 0}
                      onPress={() => router.push(`/system/${s.id}` as never)}
                    />
                  ))
              )}
            </View>
          </View>
        </View>

        {/* Single "View all systems" CTA — covers both lists since
            the /systems page filters by Inside / Outside chips. */}

        {(inside.length > PREVIEW_COUNT || outside.length > PREVIEW_COUNT) ? (
          <Pressable
            onPress={() => router.push('/systems' as never)}
            style={({ pressed }) => [styles.viewAllBtn, pressed && { opacity: 0.85 }]}>
            <Text style={styles.viewAllText}>
              View all {inside.length + outside.length} systems
            </Text>
            <ChevronRight size={14} color={Palette.forest} strokeWidth={2} />
          </Pressable>
        ) : null}

        {/* Notes — Wave 35: relocated from beside Activity to the
            bottom of the page below Systems. Full-width preview card
            that links to the dedicated /notes page for the full list. */}
        <View style={styles.notesCardWrap}>
          <NotesCard
            notes={notes}
            onAdd={() => setNoteEditor({ visible: true, note: null })}
            onOpen={(n) => setNoteEditor({ visible: true, note: n })}
            onSeeAll={() => router.push('/notes' as never)}
            limit={6}
          />
        </View>

        {/*
          Delete this home — clay-tinted danger row at the very bottom
          so it's reachable but never the obvious tap target. Disabled
          when this is the user's only home (homeCount === 1) so we
          never strand them in an empty-app state; copy in onDelete
          explains the gate when they try.
        */}
        <Pressable
          onPress={onDeleteHome}
          style={({ pressed }) => [
            styles.deleteBtn,
            pressed && { opacity: 0.85 },
            homeCount <= 1 && { opacity: 0.45 },
          ]}>
          <Trash2 size={14} color={Palette.clay} strokeWidth={2} />
          <Text style={styles.deleteBtnText}>
            {homeCount <= 1 ? 'Delete (add another home first)' : 'Delete this home'}
          </Text>
        </Pressable>
      </ScrollView>
      <ReplanSummaryModal
        visible={replanSummary !== null}
        diff={replanSummary}
        loading={replanLoading}
        homeName={property?.name?.trim() || 'this home'}
        onClose={() => {
          setReplanSummary(null);
          setReplanLoading(false);
        }}
        onConfirm={onConfirmReplan}
      />
      <NoteEditorModal
        visible={noteEditor.visible}
        note={noteEditor.note}
        onClose={() => setNoteEditor({ visible: false, note: null })}
        onSave={async (input) => {
          if (!property) return;
          if (noteEditor.note) {
            await updateNote(db, noteEditor.note.id, input);
          } else {
            await addNote(db, {
              propertyId: property.id,
              title: input.title,
              body: input.body,
              pinned: input.pinned,
            });
          }
          // Re-fetch via shared loader so Activity sees the cross-data
          // bump too (notes show in /timeline alongside completions).
          bumpDataRevision();
          const fresh = await listNotes(db, property.id, 50);
          setNotes(fresh);
        }}
        onDelete={
          noteEditor.note
            ? async () => {
                if (!noteEditor.note || !property) return;
                await deleteNote(db, noteEditor.note.id);
                bumpDataRevision();
                const fresh = await listNotes(db, property.id, 50);
                setNotes(fresh);
              }
            : undefined
        }
      />
    </View>
  );
}

/** Small circular icon button used in the My Home title row. Optional
 *  badge for the Tune button. The `tone` prop swaps the icon color
 *  (default forest, honey for Tune to mirror its sparkle theme). */
function TitleIconBtn({
  Icon,
  accessibilityLabel,
  /** Small text label rendered below the icon button. Optional, but
   *  passing one is encouraged so the affordance reads as a labeled
   *  toolbar rather than four mystery glyphs. */
  label,
  onPress,
  tone = 'forest',
  badge,
}: {
  Icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
  accessibilityLabel: string;
  label?: string;
  onPress: () => void;
  tone?: 'forest' | 'honey';
  badge?: number;
}) {
  const color = tone === 'honey' ? Palette.honeyDeep : Palette.forest;
  return (
    <Pressable
      onPress={onPress}
      accessibilityLabel={accessibilityLabel}
      hitSlop={6}
      style={({ pressed }) => [
        styles.titleIconStack,
        pressed && { transform: [{ scale: 0.94 }], opacity: 0.85 },
      ]}>
      <View style={styles.titleIconBtn}>
        <Icon size={17} color={color} strokeWidth={1.9} />
        {badge && badge > 0 ? (
          <View style={styles.titleIconBadge}>
            <Text style={styles.titleIconBadgeText}>{badge > 9 ? '9+' : badge}</Text>
          </View>
        ) : null}
      </View>
      {label ? (
        <Text style={styles.titleIconLabel} numberOfLines={1}>
          {label}
        </Text>
      ) : null}
    </Pressable>
  );
}

/** Document tile used in the merged home card. Carries a small "PDF"
 *  badge in the top-right corner so the user can see at a glance these
 *  are printable documents — replaces the explicit section header
 *  that we dropped. */
function ImportantDocTile({
  label,
  sub,
  Icon,
  bg,
  onPress,
  disabled,
}: {
  label: string;
  sub: string;
  Icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
  bg: string;
  onPress: () => void;
  disabled?: boolean;
}) {
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      style={({ pressed }) => [
        styles.importantDocTile,
        pressed && { transform: [{ scale: 0.98 }] },
        disabled && { opacity: 0.55 },
      ]}>
      <View style={[styles.importantDocIcon, { backgroundColor: bg }]}>
        <Icon size={20} color={Palette.cream} strokeWidth={2} />
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={styles.importantDocLabel} numberOfLines={1}>{label}</Text>
        <Text style={styles.importantDocSub} numberOfLines={2}>{sub}</Text>
      </View>
      <View style={styles.pdfBadge}>
        <Text style={styles.pdfBadgeText}>PDF</Text>
      </View>
    </Pressable>
  );
}

/** Compact tile component used by the Resources grid. Same visual
 *  vocabulary as the Settings Tools tile but a touch smaller so the
 *  grid sits naturally inside the My Home rhythm. */
function ResourceTile({
  label,
  Icon,
  bg,
  onPress,
  disabled,
}: {
  label: string;
  Icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
  bg: string;
  onPress: () => void;
  disabled?: boolean;
}) {
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      style={({ pressed }) => [
        styles.resourceTile,
        pressed && { transform: [{ scale: 0.96 }] },
        disabled && { opacity: 0.55 },
      ]}>
      <View style={[styles.resourceIcon, { backgroundColor: bg }]}>
        <Icon size={18} color={Palette.cream} strokeWidth={2} />
      </View>
      <Text style={styles.resourceLabel} numberOfLines={2}>{label}</Text>
    </Pressable>
  );
}

/** One label/value cell inside a 2-column summary row. */
function SummaryCell({ label, value }: { label: string; value: string }) {
  return (
    <View style={styles.summaryCell}>
      <Text style={styles.summaryLabel}>{label.toUpperCase()}</Text>
      <Text style={styles.summaryValue} numberOfLines={1}>{value}</Text>
    </View>
  );
}

/**
 * Cap on how many system rows each location's preview list shows on
 * My Home. Anything past this lives on the dedicated /systems page
 * which has search + filtering + multi-select. Tuning knob: 5 fits
 * comfortably above the fold on most phones without crowding the
 * property summary at the top.
 */
const PREVIEW_COUNT = 5;

/**
 * Activity row rendered inside the My Home Recent activity card. Each
 * row has a kind-tinted icon dot, a title, and a sub-line with the
 * subtitle + relative time. Same kind→meta map the /timeline page uses,
 * inlined here so we don't have to expose KIND_META as a public surface
 * just for one consumer.
 */
const ACTIVITY_KIND_META: Record<TimelineKind, { tint: string; bg: string; tag: string }> = {
  completion: { tint: Palette.forest, bg: '#DCE7E1', tag: 'Done' },
  skip: { tint: Palette.clay, bg: 'rgba(176,95,60,0.13)', tag: 'Skipped' },
  expense: { tint: Palette.honeyDeep, bg: 'rgba(225,163,61,0.18)', tag: 'Expense' },
  system: { tint: Palette.forest, bg: '#EAF0EA', tag: 'System' },
  inventory: { tint: Palette.honeyDeep, bg: 'rgba(225,163,61,0.14)', tag: 'Inventory' },
  document: { tint: Palette.clay, bg: 'rgba(176,95,60,0.13)', tag: 'Doc' },
  contact: { tint: Palette.forest, bg: '#EAF0EA', tag: 'Contact' },
  note: { tint: Palette.honeyDeep, bg: Palette.honeySoft, tag: 'Note' },
  quote: { tint: Palette.forestGlow, bg: '#DCE7E1', tag: 'Quote' },
};

function ActivityRow({
  entry,
  onPress,
}: {
  entry: TimelineEntry;
  onPress: () => void;
}) {
  const meta = ACTIVITY_KIND_META[entry.kind];
  const when = relativeShort(entry.at);
  return (
    <Pressable
      accessibilityLabel={`Open ${entry.title}`}
      onPress={onPress}
      style={({ pressed, hovered }) => [
        styles.activityRow,
        // Hover surfaces a warm sand pill — same hover color we use on
        // settings rows and contact rows, so the affordance reads as
        // "navigate" rather than "select" (which is the Notes idiom).
        // eslint-disable-next-line react-native/no-inline-styles
        hovered ? { backgroundColor: 'rgba(231,218,195,0.45)' } : null,
        pressed && { opacity: 0.88 },
      ]}>
      <View style={[styles.activityKindDot, { backgroundColor: meta.bg }]}>
        <View style={[styles.activityKindCore, { backgroundColor: meta.tint }]} />
      </View>
      <View style={styles.activityRowBody}>
        <View style={styles.activityRowTitleLine}>
          <Text style={styles.activityRowTag}>{meta.tag}</Text>
          <Text style={styles.activityRowTitle} numberOfLines={1}>
            {entry.title || 'Untitled'}
          </Text>
        </View>
        {entry.subtitle ? (
          <Text style={styles.activityRowSub} numberOfLines={1}>
            {entry.subtitle}
          </Text>
        ) : null}
      </View>
      <Text style={styles.activityRowWhen}>{when}</Text>
    </Pressable>
  );
}

function relativeShort(ts: number): string {
  const diff = Date.now() - ts;
  const day = 24 * 60 * 60 * 1000;
  if (diff < 60_000) return 'now';
  if (diff < 60 * 60_000) return `${Math.floor(diff / 60_000)}m`;
  if (diff < day) return `${Math.floor(diff / (60 * 60_000))}h`;
  if (diff < 7 * day) return `${Math.floor(diff / day)}d`;
  return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function navigateForActivity(
  router: ReturnType<typeof useRouter>,
  e: TimelineEntry
): void {
  switch (e.kind) {
    case 'completion':
    case 'skip':
      router.push(`/task/${e.ref_id}` as never);
      break;
    case 'expense':
      router.push(`/expenses/${e.ref_id}` as never);
      break;
    case 'system':
      router.push(`/system/${e.ref_id}` as never);
      break;
    case 'inventory':
      router.push(`/inventory/${e.ref_id}` as never);
      break;
    case 'contact':
      router.push(`/contact/${e.ref_id}` as never);
      break;
    case 'document':
      router.push('/media' as never);
      break;
    case 'note':
      router.push({ pathname: '/notes', params: { focus: e.ref_id } } as never);
      break;
    case 'quote':
      router.push(`/quotes/${e.ref_id}` as never);
      break;
  }
}

function EmptyHint({ children }: { children: React.ReactNode }) {
  return <Text style={styles.emptyHint}>{children}</Text>;
}

function QuickBtn({
  label,
  Icon,
  onPress,
  primary,
}: {
  label: string;
  Icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
  onPress: () => void;
  primary?: boolean;
}) {
  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => [styles.quickBtn, pressed && { transform: [{ scale: 0.95 }] }]}>
      <View style={[styles.quickIco, primary && { backgroundColor: Palette.forest }]}>
        <Icon size={18} color={primary ? Palette.cream : Palette.forest} strokeWidth={1.8} />
      </View>
      <Text style={styles.quickLabel} numberOfLines={1}>{label}</Text>
    </Pressable>
  );
}

function SystemRowView({
  system,
  reminderCount = 0,
  onPress,
}: {
  system: SystemRow;
  reminderCount?: number;
  onPress: () => void;
}) {
  const meta = getSystemMeta(system.kind);
  const subtype = getSubtypeMeta(system.kind, system.subtype);
  const Icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }> = resolveIcon(meta?.icon ?? null);

  const detailBits = [
    subtype?.label,
    system.model,
    system.filter_size,
    system.installed_year ? `installed ${system.installed_year}` : null,
  ].filter(Boolean);
  const detail = detailBits.join(' · ');
  const seasonal = seasonalLabel(
    { startMonth: system.active_start_month, endMonth: system.active_end_month },
    system.is_active === 1
  );
  const isDisabled = system.is_active === 0;
  const hasDetail = detailBits.length > 0;

  return (
    <Pressable
      accessibilityRole="button"
      onPress={onPress}
      style={({ pressed }) => [
        styles.sysRow,
        pressed && { transform: [{ scale: 0.99 }] },
        isDisabled && { opacity: 0.5 },
      ]}>
      <View style={styles.sysIco}>
        <Icon size={20} color={Palette.forest} strokeWidth={1.7} />
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
          <Text style={styles.sysTitle} numberOfLines={1}>
            {system.nickname ?? meta?.label ?? system.kind}
          </Text>
          {seasonal ? (
            <View
              style={[
                styles.seasonalBadge,
                isDisabled && { backgroundColor: 'rgba(176,95,60,0.18)' },
              ]}>
              <Text
                style={[
                  styles.seasonalBadgeText,
                  isDisabled && { color: Palette.clay },
                ]}>
                {seasonal}
              </Text>
            </View>
          ) : null}
          {reminderCount > 0 ? (
            <View style={styles.reminderBadge}>
              <Text style={styles.reminderBadgeText}>
                +{reminderCount} reminder{reminderCount === 1 ? '' : 's'}
              </Text>
            </View>
          ) : null}
        </View>
        {hasDetail ? (
          <Text style={styles.sysDetail} numberOfLines={2}>
            {detail}
          </Text>
        ) : (
          <Text style={styles.sysDetailFaint}>Add details to unlock matching tasks →</Text>
        )}
      </View>
      <ChevronRight size={18} color={Palette.inkFaint} strokeWidth={1.7} />
    </Pressable>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  header: { paddingHorizontal: 22, paddingTop: Spacing.three },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  // h1 takes the rest of the row; numberOfLines={1} on the Text + this
  // flex shrink prevents long nicknames from pushing the icon menu off
  // the right edge. The input that captures the nickname (onboarding
  // step 2 + edit form) caps at 30 chars for the same reason.
  h1: {
    flex: 1,
    minWidth: 0,
    fontFamily: Fonts.serif,
    fontSize: 27,
    color: Palette.ink,
    letterSpacing: -0.3,
  },
  titleIcons: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 4,
  },
  // Wraps each icon button + its tiny label as a vertical stack.
  titleIconStack: {
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 2,
  },
  // Sub-label below each title icon ("Edit", "Add home", "Tune",
  // "Replan"). Small + muted so it explains the glyph without competing
  // with the home name to its left.
  titleIconLabel: {
    fontFamily: Fonts.sansBold,
    fontSize: 11,
    lineHeight: 11,
    // Forest green to match the Schedule submenu styling — the user's
    // canonical look. Applied across My Home / Schedule / Today /
    // Settings so all four submenus read as one cross-page system.
    color: Palette.forest,
    letterSpacing: 0.2,
    maxWidth: 60,
    textAlign: 'center',
  },
  titleIconBtn: {
    width: 34,
    height: 34,
    borderRadius: 17,
    borderWidth: 1,
    borderColor: Palette.line,
    backgroundColor: Palette.card,
    alignItems: 'center',
    justifyContent: 'center',
  },
  titleIconBadge: {
    position: 'absolute',
    top: -3,
    right: -3,
    minWidth: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: Palette.honeyDeep,
    paddingHorizontal: 4,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1.5,
    borderColor: Palette.sand,
  },
  titleIconBadgeText: {
    fontFamily: Fonts.sansBold,
    fontSize: 10.5,
    color: Palette.cream,
    letterSpacing: 0.2,
  },
  sub: { marginTop: 3, fontSize: 13, color: Palette.inkSoft, fontFamily: Fonts.sansMedium },

  // ---- Add system button (top position, just under the quick toolbar) ----
  addBtnTop: {
    marginTop: Spacing.three,
    marginHorizontal: 18,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: Palette.forest,
    paddingVertical: 14,
    borderRadius: 16,
  },

  // ---- Merged "your home" card ----
  // Info on the left, documents on the right (web), stacked on mobile.
  // Single outer container with a vertical divider that becomes a
  // horizontal divider when the card wraps — visually reads as one
  // home record split into two zones.
  homeCard: {
    marginTop: Spacing.three,
    marginHorizontal: 18,
    flexDirection: 'row',
    flexWrap: 'wrap',
    backgroundColor: Palette.card,
    borderRadius: Radius.xl,
    borderWidth: 1,
    borderColor: Palette.line,
    overflow: 'hidden',
  },
  homeCardInfo: {
    flex: 1,
    minWidth: 280,
    paddingHorizontal: 16,
    paddingVertical: 6,
  },
  homeCardDivider: {
    width: 1,
    alignSelf: 'stretch',
    backgroundColor: Palette.line,
  },
  homeCardDocs: {
    flex: 1,
    minWidth: 280,
    padding: 12,
    gap: 8,
  },

  // ---- Document tile (used inside homeCardDocs) ----
  importantDocTile: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 12,
    backgroundColor: '#FBF6EA',
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: Radius.lg,
    position: 'relative',
  },
  importantDocIcon: {
    width: 38,
    height: 38,
    borderRadius: 19,
    alignItems: 'center',
    justifyContent: 'center',
  },
  importantDocLabel: {
    fontFamily: Fonts.sansBold,
    fontSize: 14,
    color: Palette.ink,
    letterSpacing: -0.1,
  },
  importantDocSub: {
    marginTop: 2,
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.inkSoft,
    lineHeight: 16,
  },
  // Small "PDF" pill in the corner — tells users this opens a printable
  // document, not just another screen. Forest-on-honey for the calm
  // contrast pairing we use elsewhere on the page.
  pdfBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 6,
    backgroundColor: Palette.forest,
  },
  pdfBadgeText: {
    fontFamily: Fonts.sansBold,
    fontSize: 8.5,
    color: Palette.cream,
    letterSpacing: 0.6,
  },

  // ---- Shortcut pill row (Timeline only now) ----
  shortcutRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 8,
  },
  // Recent activity card — mini-timeline preview that links to the
  // full /timeline route. Header is a Pressable; rows render with a
  // simple left rail (dot + line) so the section reads as a chronology
  // at a glance.
  // Activity card is now full-width (Wave 35). Margins live here so we
  // don't depend on a wrapper row anymore.
  activityCard: {
    marginTop: Spacing.four,
    marginHorizontal: 18,
    padding: Spacing.four,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: 18,
  },
  // Notes card wrapper for the bottom-of-page placement. Same horizontal
  // gutter as the rest of the page sections.
  notesCardWrap: {
    marginTop: Spacing.four,
    marginHorizontal: 18,
  },
  activityHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  activityHeadLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  activityHeadIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#EAF0EA',
    alignItems: 'center',
    justifyContent: 'center',
  },
  activityHeadTitle: {
    fontFamily: Fonts.serifBold,
    fontSize: 16,
    color: Palette.ink,
    letterSpacing: -0.2,
  },
  activityHeadSub: {
    marginTop: 1,
    fontFamily: Fonts.sansMedium,
    fontSize: 12.5,
    color: Palette.inkFaint,
  },
  activityHeadRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
  },
  activityHeadLink: {
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    color: Palette.forest,
    letterSpacing: 0.2,
  },
  activityEmpty: {
    marginTop: Spacing.three,
    padding: 12,
    borderRadius: 12,
    backgroundColor: '#F4F0E2',
  },
  activityEmptyText: {
    fontFamily: Fonts.sansMedium,
    fontSize: 13.5,
    color: Palette.inkSoft,
    lineHeight: 17,
  },
  // Wave 35: replaces the rail-and-dot timeline list with a flex-wrap
  // 2-column grid. Wide screens get two side-by-side columns of rows;
  // narrow screens collapse to a single column via flexBasis 100%.
  activityGrid: {
    marginTop: Spacing.three,
    flexDirection: 'row',
    flexWrap: 'wrap',
    columnGap: Spacing.two,
    rowGap: 6,
  },
  // Activity row — Wave 36 restyle: visually distinct from Notes
  // (which uses warm sand pills). Activity rows are forest-leaning,
  // tighter, no background fill — they read as a timeline of small
  // events instead of a list of cards. Hover lifts a tinted bg so the
  // row stays interactive on desktop.
  activityRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 10,
    paddingHorizontal: Spacing.three,
    backgroundColor: 'transparent',
    borderWidth: 0,
    borderBottomWidth: 1,
    borderBottomColor: Palette.line,
    borderRadius: 0,
    flexBasis: '48%',
    flexGrow: 1,
    minWidth: 220,
  },
  // Larger ring so the kind tint reads as a status marker, not a
  // bullet point. Inner core is a smaller filled dot to keep visual
  // weight balanced with the title text.
  activityKindDot: {
    width: 26,
    height: 26,
    borderRadius: 13,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    borderWidth: 1.5,
    borderColor: Palette.line,
  },
  activityKindCore: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  activityRowBody: {
    flex: 1,
    minWidth: 0,
  },
  activityRowTitleLine: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  activityRowTag: {
    fontFamily: Fonts.sansBold,
    fontSize: 10.5,
    color: Palette.inkSoft,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    // Tag column has a min width so titles align after the badge text.
    minWidth: 40,
  },
  activityRowTitle: {
    flex: 1,
    fontFamily: Fonts.sansSemibold,
    fontSize: 13,
    color: Palette.ink,
    letterSpacing: -0.05,
  },
  activityRowSub: {
    marginTop: 1,
    fontFamily: Fonts.sansMedium,
    fontSize: 12,
    color: Palette.inkFaint,
  },
  activityRowWhen: {
    fontFamily: Fonts.sansBold,
    fontSize: 11.5,
    color: Palette.inkFaint,
    letterSpacing: 0.2,
    flexShrink: 0,
  },
  // Legacy `summary` style kept as an alias of homeCardInfo for now —
  // any code path still referencing styles.summary continues to work.
  summary: {
    flex: 1,
    minWidth: 280,
  },

  // ---- Inside / Outside two-column row (web) ----
  systemsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  systemsCol: {
    flex: 1,
    minWidth: 320,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: Palette.line,
  },
  summaryLabel: {
    fontFamily: Fonts.sansBold,
    fontSize: 12,
    color: Palette.inkFaint,
    letterSpacing: 0.6,
  },
  summaryValue: {
    fontFamily: Fonts.sansSemibold,
    fontSize: 13.5,
    color: Palette.ink,
  },
  // 2-column variant — each row holds two label/value cells side by
  // side. Label sits above value (stacked) inside each cell so the
  // value can be longer without colliding with its neighbor.
  summaryRow2: {
    flexDirection: 'row',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: Palette.line,
    gap: 12,
  },
  summaryCell: {
    flex: 1,
    minWidth: 0,
  },

  // ---- Resources grid (My Home) ----
  // Same vocabulary as the Settings Tools grid but slimmer tiles so
  // 7 tiles fit comfortably in a 4-col layout on mobile (two rows) and
  // a 7-col layout on web (single row). Centered on every viewport.
  resourcesGrid: {
    marginTop: Spacing.three,
    marginHorizontal: 14,
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    columnGap: 8,
    rowGap: 10,
  },
  resourceTile: {
    width: '22%',
    maxWidth: 84,
    aspectRatio: 1,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
    paddingVertical: 8,
    gap: 6,
  },
  resourceIcon: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  resourceLabel: {
    fontFamily: Fonts.sansBold,
    fontSize: 11,
    color: Palette.ink,
    textAlign: 'center',
    lineHeight: 12,
  },

  // ---- Delete-home button ----
  // Tucked at the very bottom of My Home — visible but never the
  // obvious target. Clay text + transparent bg keeps it from competing
  // visually with the green primary actions.
  deleteBtn: {
    marginTop: Spacing.four,
    marginHorizontal: 18,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(176,95,60,0.35)',
    backgroundColor: 'rgba(176,95,60,0.06)',
  },
  deleteBtnText: {
    fontFamily: Fonts.sansBold,
    fontSize: 13.5,
    color: Palette.clay,
    letterSpacing: 0.2,
  },
  addRow: { paddingHorizontal: 18, marginTop: Spacing.three },
  // "View all systems" CTA at the bottom of the My Home systems
  // previews. Subtle pill, mid-page — clearly a link, not a primary
  // action.
  viewAllBtn: {
    marginHorizontal: 18,
    marginTop: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Palette.line,
    backgroundColor: Palette.card,
  },
  viewAllText: {
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    color: Palette.forest,
    letterSpacing: 0.2,
  },
  addBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: Palette.forest,
    paddingVertical: 14,
    borderRadius: 16,
  },
  addText: {
    color: Palette.cream,
    fontFamily: Fonts.sansBold,
    fontSize: 14,
  },
  subBtn: {
    flex: 1,
    minWidth: 140,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    paddingVertical: 11,
    paddingHorizontal: 12,
    borderRadius: 14,
  },
  subBtnText: {
    flex: 1,
    color: Palette.ink,
    fontFamily: Fonts.sansBold,
    fontSize: 13.5,
  },
  seasonalBadge: {
    backgroundColor: '#DCE7E1',
    paddingHorizontal: 7,
    paddingVertical: 2,
    borderRadius: 6,
  },
  reminderBadge: {
    backgroundColor: 'rgba(225,163,61,0.18)',
    paddingHorizontal: 7,
    paddingVertical: 2,
    borderRadius: 6,
  },
  reminderBadgeText: {
    fontSize: 11,
    fontFamily: 'HankenGrotesk_700Bold',
    color: Palette.honeyDeep,
    letterSpacing: 0.3,
  },
  seasonalBadgeText: {
    fontSize: 11,
    fontFamily: Fonts.sansBold,
    color: Palette.forest,
    letterSpacing: 0.3,
  },
  list: {
    paddingHorizontal: 18,
    gap: 8,
  },
  sysRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: 16,
    padding: 12,
  },
  sysIco: {
    width: 38,
    height: 38,
    borderRadius: 11,
    backgroundColor: '#EAF0EA',
    alignItems: 'center',
    justifyContent: 'center',
  },
  sysTitle: { fontSize: 14, fontFamily: Fonts.sansBold, color: Palette.ink },
  sysDetail: { marginTop: 2, fontSize: 12.5, fontFamily: Fonts.sansMedium, color: Palette.inkSoft },
  sysDetailFaint: {
    marginTop: 2,
    fontSize: 12,
    fontFamily: Fonts.sansSemibold,
    color: Palette.inkFaint,
  },
  emptyHint: {
    paddingVertical: 10,
    fontSize: 13.5,
    color: Palette.inkFaint,
    fontFamily: Fonts.sansSemibold,
    textAlign: 'center',
  },
  quickRow: {
    paddingHorizontal: 14,
    paddingTop: Spacing.three,
    gap: 4,
    flexDirection: 'row',
  },
  quickBtn: {
    alignItems: 'center',
    gap: 5,
    width: 56,
    // flexShrink: 0 keeps each button at its full 56px width even when
    // the parent ScrollView's content-container becomes flex-row in a
    // narrower layout — guarantees horizontal scroll instead of items
    // squishing/wrapping under each other.
    flexShrink: 0,
  },
  quickIco: {
    width: 44,
    height: 44,
    borderRadius: 14,
    backgroundColor: '#EAF0EA',
    borderWidth: 1,
    borderColor: Palette.line,
    alignItems: 'center',
    justifyContent: 'center',
  },
  quickLabel: {
    fontFamily: Fonts.sansBold,
    fontSize: 11.5,
    color: Palette.ink,
    textAlign: 'center',
    lineHeight: 13,
  },
});
